import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import _ from 'lodash';

import { Editor, EditorState, RichUtils, ContentState } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import PropTypes from 'prop-types';

const RichTextInput = forwardRef(function RichTextInput({ saveText }, ref) {
	const [state, setState] = useState({
		identifier: false,
		isVisible: false,
		editorState: EditorState.createEmpty()
	});

	const editorRef = useRef(null);

	const onChange = useCallback((editorState) => {
		setState(prevState => ({ ...prevState, editorState }));
	}, []);

	const onFocus = useCallback(() => {
		if (editorRef.current) {
			editorRef.current.focus();
		}
	}, []);

	const richText_close = useCallback((event) => {
		if (event) {
			event.preventDefault();
		}

		setState(prevState => ({
			...prevState,
			identifier: false,
			isVisible: false
		}));
	}, []);

	const richText_discard = useCallback((event) => {
		if (event) {
			event.preventDefault();
		}

		const editorState = EditorState.push(state.editorState, ContentState.createFromText(''));
		setState({
			...state,
			identifier: false,
			isVisible: false,
			editorState: editorState
		});
	}, [state]);

	const richText_save = useCallback((event) => {
		if (event) {
			event.preventDefault();
		}

		let html = stateToHTML(state.editorState.getCurrentContent());

		if (html == '<p><br></p>') {
			richText_discard(false);
		} else {
			saveText(false, html, state.identifier, () => { richText_discard(false); });
		}
	}, [state, saveText, richText_discard]);

	const goRichText = useCallback((event, theIdentifier, theHTML) => {
		if (event) {
			event.preventDefault();
		}

		document.closeModalExtra = (e) => {
			richText_close(false);
		};

		if (theHTML) {
			let contentState = stateFromHTML(theHTML);
			const editorState = EditorState.createWithContent(contentState);
			setState(prevState => ({
				...prevState,
				isVisible: false,
				editorState: editorState
			}));

			setState(prevState => ({
				...prevState,
				identifier: theIdentifier,
				isVisible: true
			}), () => {
				setTimeout(() => {
					if (editorRef.current) {
						editorRef.current.focus();
					}
				}, 0);
			});
		} else {
			const editorState = EditorState.push(state.editorState, ContentState.createFromText(''));
			setState({
				...state,
				identifier: false,
				isVisible: false,
				editorState: editorState
			});

			setState(prevState => ({
				...prevState,
				identifier: theIdentifier,
				isVisible: true
			}), () => {
				setTimeout(() => {
					if (editorRef.current) {
						editorRef.current.focus();
					}
				}, 0);
			});
		}
	}, [state, richText_close]);

	const richTextButton = useCallback((event, theStyle) => {
		if (event) {
			event.preventDefault();
		}
		onChange(RichUtils.toggleInlineStyle(state.editorState, theStyle));
	}, [state, onChange]);

	useImperativeHandle(ref, () => ({
		goRichText
	}), [goRichText]);

	let currentStyle = state.editorState.getCurrentInlineStyle();

	const styleMap = {
		'CODE': {
			backgroundColor: '#ffcc6d'
		}
	};

	return (
		<div className={state.isVisible ? "c-modal c-modal--visible" : "c-modal"}>
			<div className="c-modal__bg js-close-modal"></div>
			<div className="c-modal__inner ">
				<button className="c-modal-close-btn" onClick={richText_discard}>
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<path d="M18 6 6 18"></path>
						<path d="m6 6 12 12"></path>
					</svg>
				</button>
				<h5 className='c-modal-title'>Add note</h5>
				<div className="toolbar">
					<button className={currentStyle && currentStyle.has('BOLD') ? "toolbar-btn active" : "toolbar-btn"} onMouseDown={(e) => { richTextButton(e, 'BOLD') }} onClick={(e) => { e.preventDefault() }}>B</button>
					<button className={currentStyle && currentStyle.has('ITALIC') ? "toolbar-btn active" : "toolbar-btn"} onMouseDown={(e) => { richTextButton(e, 'ITALIC') }} onClick={(e) => { e.preventDefault() }}><i>I</i></button>
					<button className={currentStyle && currentStyle.has('STRIKETHROUGH') ? "toolbar-btn active u-text-strikethrough" : "toolbar-btn u-text-strikethrough"} onMouseDown={(e) => { richTextButton(e, 'STRIKETHROUGH') }} onClick={(e) => { e.preventDefault() }}>S</button>
					<button className={currentStyle && currentStyle.has('UNDERLINE') ? "toolbar-btn active u-text-underline" : "toolbar-btn u-text-underline"} onMouseDown={(e) => { richTextButton(e, 'UNDERLINE') }} onClick={(e) => { e.preventDefault() }}>U</button>
				</div>
				<div className="c-rich-text-input" onClick={onFocus}>
					<Editor editorState={state.editorState} onChange={onChange} customStyleMap={styleMap} ref={editorRef} placeholder="Type your note here..." />
				</div>
				<div className='c-modal-footer'>
					<button className="btn-sm c-btn--grey " onClick={richText_discard}>Discard</button>
					<button className="btn-sm c-btn--green" onClick={richText_save}>Save note</button>
				</div>

			</div>
		</div >
	);
});

RichTextInput.contextTypes = {
	router: PropTypes.object
}

RichTextInput.propTypes = {
	saveText: PropTypes.func.isRequired
}

RichTextInput.displayName = 'RichTextInput';

export default RichTextInput;